import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { AnnouncementsPopup } from "@/components/announcements/AnnouncementsPopup";
import {
  House,
  CheckSquare,
  User,
  SignOut,
  Lightning,
  ChatCircleDots,
  Handshake,
  DotsThree,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useUnreadMessages } from "@/lib/useUnreadMessages";
import NotificationBell from "@/components/NotificationBell";
import InstallAppBanner from "@/components/pwa/InstallAppBanner";
import { registerWorkerServiceWorker } from "@/lib/pwaRegister";
import { useSeo, NOINDEX } from "@/lib/useSeo";

// Primary tabs shown in the bottom bar (4 slots)
const primaryTabs = [
  { to: "/crew", label: "Feed", icon: House, end: true },
  { to: "/crew/my-assignments", label: "My work", icon: CheckSquare, end: false },
  { to: "/crew/messages", label: "Messages", icon: ChatCircleDots, end: false, badge: "messages" },
];

// Items behind the "More" dropdown
const moreItems = [
  { to: "/crew/refer", label: "Refer · earn 10%", icon: Handshake, end: false },
  { to: "/crew/me", label: "Profile", icon: User, end: false },
];

export default function WorkerLayout() {
  useSeo({ title: "Crew — HCOB", path: "/crew", robots: NOINDEX });
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const { count: messagesUnread } = useUnreadMessages();
  const [moreOpen, setMoreOpen] = useState(false);

  // Register the /crew-scoped service worker + inject the mobile PWA meta
  // tags so tapping "Install" gives a real standalone app icon on both
  // Android (Chrome native prompt) and iOS (Share -> Add to Home Screen).
  useEffect(() => {
    registerWorkerServiceWorker();
  }, []);

  const onLogout = async () => {
    await logout();
    nav("/", { replace: true });
  };

  const isMoreActive = moreItems.some((m) =>
    m.end ? location.pathname === m.to : location.pathname.startsWith(m.to)
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="worker-layout">
      <div className="mx-auto flex min-h-screen max-w-md flex-col bg-[#F9FAFB] shadow-[0_0_0_1px_#E5E7EB]">
        <header className="pt-safe sticky top-0 z-10 flex items-center justify-between border-b border-[#E5E7EB] bg-white px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="grid h-7 w-7 place-items-center bg-[#030712] text-white">
              <Lightning weight="fill" size={14} />
            </div>
            <div>
              <div className="font-display text-base font-black leading-none">HCOB Network</div>
              <div className="font-mono-label text-[9px]">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell variant="light" homePath="/crew" />
            <button
              data-testid="worker-logout-btn"
              onClick={onLogout}
              className="grid h-9 w-9 place-items-center border border-[#E5E7EB] text-[#030712] hover:bg-[#030712] hover:text-white"
              aria-label="sign out"
            >
              <SignOut size={16} />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-24">
          <InstallAppBanner />
          <AnnouncementsPopup />
          <Outlet />
        </main>

        <nav
          data-testid="worker-bottom-nav"
          className="pb-safe sticky bottom-0 z-10 grid grid-cols-4 border-t border-[#E5E7EB] bg-white"
        >
          {primaryTabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`tab-${t.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-1 py-3 text-[10px] font-semibold uppercase tracking-widest ${
                  isActive ? "text-[#0044FF]" : "text-[#4B5563]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="relative">
                    <t.icon size={22} weight={isActive ? "fill" : "duotone"} />
                    {t.badge === "messages" && messagesUnread > 0 && (
                      <span
                        data-testid="worker-tab-messages-badge"
                        className="absolute -right-2 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center bg-[#F59E0B] px-1 text-[9px] font-bold tracking-widest text-white"
                      >
                        {messagesUnread > 99 ? "99+" : messagesUnread}
                      </span>
                    )}
                  </div>
                  {t.label}
                </>
              )}
            </NavLink>
          ))}

          {/* "More" — bottom sheet with finger-sized rows (Refer + Profile) */}
          <button
            type="button"
            data-testid="tab-more"
            aria-label="More"
            aria-expanded={moreOpen}
            aria-controls="worker-more-sheet"
            onClick={() => setMoreOpen(true)}
            className={`relative flex flex-col items-center justify-center gap-1 py-3 text-[10px] font-semibold uppercase tracking-widest ${
              isMoreActive || moreOpen ? "text-[#0044FF]" : "text-[#4B5563]"
            }`}
          >
            <DotsThree size={26} weight="bold" />
            More
          </button>
        </nav>

        {moreOpen && (
          <div
            className="fixed inset-0 z-30 flex items-end justify-center bg-[#030712]/40"
            onClick={() => setMoreOpen(false)}
            data-testid="worker-more-backdrop"
          >
            <div
              id="worker-more-sheet"
              role="dialog"
              aria-label="More"
              data-testid="worker-more-menu"
              onClick={(e) => e.stopPropagation()}
              className="pb-safe w-full max-w-md animate-in slide-in-from-bottom-4 rounded-t-2xl bg-white shadow-2xl duration-200"
            >
              <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-[#E5E7EB]" aria-hidden />
              <div className="px-4 pb-3 pt-3">
                {moreItems.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    onClick={() => setMoreOpen(false)}
                    data-testid={`more-${it.label.toLowerCase().replace(/[ ·]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex min-h-[56px] items-center gap-4 rounded-lg px-3 text-[15px] ${
                        isActive
                          ? "bg-[#F0F4FF] font-semibold text-[#030712]"
                          : "text-[#030712] active:bg-[#F3F4F6]"
                      }`
                    }
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#F3F4F6] text-[#030712]">
                      <it.icon size={20} weight="duotone" />
                    </span>
                    <span className="flex-1">{it.label}</span>
                  </NavLink>
                ))}
                <button
                  type="button"
                  onClick={() => setMoreOpen(false)}
                  data-testid="worker-more-close"
                  className="mt-2 flex min-h-[48px] w-full items-center justify-center rounded-lg border border-[#E5E7EB] text-sm font-semibold text-[#4B5563]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
